import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, X, Shield, QrCode, ChevronDown, ChevronUp, Copy, CheckCircle2 } from 'lucide-react';
import { N8N_LOGIN_URL, BASE_URL } from '../constants';
import { Button, Card, Input } from './ui';
import { getCsrfToken } from '../utils';

export const LoginModal = ({ onClose, onLogin }) => {
  const { t } = useTranslation();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [preAuthToken, setPreAuthToken] = useState('');
  const [qrBase64, setQrBase64] = useState('');
  const [secret, setSecret] = useState('');
  const [step, setStep] = useState('credentials'); // credentials | setup | totp
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSecretHint, setShowSecretHint] = useState(false);
  const [copied, setCopied] = useState(false);

  // Текст ошибки входа: бэкенд присылает код (error) и русское сообщение (message).
  // Известные коды переводим сами, чтобы ошибка была на языке интерфейса;
  // message — только запасной вариант для неизвестных кодов.
  const authErrorText = (data, fallbackKey) => {
    if (data?.error === 'invalid_credentials') return t('LoginModal.invalid_login_or_password');
    if (data?.error === 'invalid_code') return t('LoginModal.invalid_code');
    if (data?.error === 'too_many_attempts') {
      const seconds = String(data.message || '').match(/\d+/)?.[0];
      return seconds
        ? t('LoginModal.too_many_attempts_wait', { seconds })
        : t('LoginModal.too_many_attempts_try_later');
    }
    return data?.message || t(fallbackKey);
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret).catch(() => {
      const el = document.createElement('textarea');
      el.value = secret;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };



  const handleCredentials = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch(N8N_LOGIN_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login, password })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        if (res.status === 429) {
          setError(authErrorText(errData, 'LoginModal.too_many_attempts_try_later'));
        } else {
          setError(authErrorText(errData, 'LoginModal.invalid_login_or_password'));
        }
        setIsLoading(false);
        return;
      }
      const data = await res.json();
      const finalData = Array.isArray(data) ? data[0] : data;

      if (finalData.status === 'totp_required') {
        setPreAuthToken(finalData.pre_auth_token);
        setStep('totp');
        return;
      }

      if (finalData.status === 'totp_setup_required') {
        setPreAuthToken(finalData.pre_auth_token);
        const qrRes = await fetch(`${BASE_URL}/api/totp/setup-for-login`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pre_auth_token: finalData.pre_auth_token })
        });
        const qrData = await qrRes.json();
        setQrBase64(qrData.qr_base64);
        setSecret(qrData.secret);
        setStep('setup');
        return;
      }

      if (finalData.status === 'success') {
        await completeLogin(finalData);
      } else {
        setError(t('LoginModal.invalid_login_or_password'));
      }
    } catch (err) {
      setError(t('LoginModal.no_server_connection'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetupVerify = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch(`${BASE_URL}/api/totp/setup-verify-login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({ pre_auth_token: preAuthToken, code: totpCode })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(authErrorText(data, 'LoginModal.invalid_code'));
        setTotpCode('');
        return;
      }
      await completeLogin(data);
    } catch (err) {
      setError(t('LoginModal.no_server_connection'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTotp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch(`${BASE_URL}/api/totp/login-verify`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({ pre_auth_token: preAuthToken, code: totpCode })
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 429) {
          setError(authErrorText(data, 'LoginModal.too_many_attempts_try_later'));
        } else {
          setError(authErrorText(data, 'LoginModal.invalid_code'));
        }
        setTotpCode('');
        return;
      }
      await completeLogin(data);
    } catch (err) {
      setError(t('LoginModal.no_server_connection'));
    } finally {
      setIsLoading(false);
    }
  };

  // После успешного логина получаем данные сессии через /auth/me
  const completeLogin = async (finalData) => {
    // Неуспешные попытки с прошлого входа — App покажет уведомление (п. 54 ФКБ)
    // Упёрлись в лимит хранения (200 на пользователя) — показываем «200+»
    const failedCount = finalData?.failed_attempts_since_last_login || 0;
    const failedAttempts = finalData?.failed_attempts_capped ? `${failedCount}+` : failedCount;
    try {
      const meRes = await fetch(`${BASE_URL}/webhook/auth/me`, {
        credentials: 'include',
      });
      if (meRes.ok) {
        const me = await meRes.json();
        onLogin({
          status: 'success',
          id: me.id,
          iris_customer_id: me.iris_customer_id,
          client_id: me.login,
          client_name: me.org_name,
          role: me.role,
          org_id: me.org_id,
          is_super_admin: me.is_super_admin,
          // момент, когда показать окно "Сессия завершена" (см. App.js)
          sessionDeadline: typeof me.session_expires_in === 'number' ? Date.now() + me.session_expires_in * 1000 : null,
          csrfToken: getCsrfToken(),
          failedAttempts,
        });
      } else {
        // Фолбек — данные из тела ответа логина
        onLogin({
          status: 'success',
          id: finalData.id,
          iris_customer_id: finalData.iris_customer_id,
          client_id: finalData.client_id,
          client_name: finalData.client_name,
          role: finalData.role,
          org_id: finalData.org_id,
          is_super_admin: finalData.is_super_admin,
          csrfToken: getCsrfToken(),
          failedAttempts,
        });
      }
    } catch {
      onLogin({
        status: 'success',
        id: finalData.id,
        iris_customer_id: finalData.iris_customer_id,
        client_id: finalData.client_id,
        client_name: finalData.client_name,
        role: finalData.role,
        org_id: finalData.org_id,
        is_super_admin: finalData.is_super_admin,
        csrfToken: getCsrfToken(),
        failedAttempts,
      });
    }
  };

  return (
    <div className="fixed inset-0 z-[150] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in duration-300">
      <Card size="xl" className="w-full max-w-md rounded-[4rem] text-center">
        <button onClick={onClose} className="absolute top-8 right-8 text-gray-400 hover:text-gray-900 transition-colors">
          <X size={24} />
        </button>

        {step === 'credentials' && (
          <>
            <div className="inline-flex p-6 bg-brand-accent rounded-xl mb-10 text-gray-900 shadow-xl shadow-brand-accent/30">
              <Lock size={40} />
            </div>
            <h2 className="text-3xl font-black text-gray-900 tracking-tighter uppercase italic leading-none mb-10">{t('LoginModal.login_to_soc')}</h2>
            
            <div className="space-y-6">
              {error && (
                <div className="p-4 bg-brand-danger border border-brand-danger-border text-brand-danger-text rounded-sm text-[11px] font-bold italic animate-in fade-in slide-in-from-top-2 duration-300">
                  {error}
                </div>
              )}
              
              <form onSubmit={handleCredentials} className="space-y-6">
                <Input
                  placeholder="client_id"
                  value={login}
                  onChange={e => setLogin(e.target.value)}
                />
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <Button type="submit" loading={isLoading} size="xl" className="w-full">
                  {t('LoginModal.authorization')}
                </Button>
              </form>
            </div>
          </>
        )}

        {step === 'setup' && (
          <>
            <div className="inline-flex p-6 bg-brand-accent rounded-xl mb-8 text-gray-900 shadow-xl shadow-brand-accent/30">
              <QrCode size={40} />
            </div>
            <h2 className="text-2xl font-black text-gray-900 tracking-tighter uppercase italic leading-none mb-3">{t('LoginModal.2fa_setup')}</h2>
            <p className="text-gray-500 text-sm mb-4">{t('LoginModal.scan_qr_in_google_authenticator')}</p>

            {qrBase64 && (
              <div className="flex justify-center mb-4">
                <img src={`data:image/png;base64,${qrBase64}`} alt={t('LoginModal.qr_code')} className="w-[220px] h-[220px]" />
              </div>
            )}

            {secret && (
              <div className="mb-5">
                <button
                  type="button"
                  onClick={() => setShowSecretHint(!showSecretHint)}
                  className="flex items-center gap-1.5 mx-auto text-gray-500 hover:text-gray-500 text-xs transition-colors"
                >
                  {showSecretHint ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  {t('LoginModal.scanning_problems')}
                </button>

                {showSecretHint && (
                  <div className="mt-3 p-4 bg-white/60 border border-gray-200 rounded-2xl text-left space-y-3">
                    <p className="text-gray-500 text-xs leading-relaxed">
                      {t('LoginModal.scan_hint_copy_code')}
                    </p>
                    <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                      <span className="font-mono text-red-600 text-sm flex-1 break-all select-all tracking-widest">
                        {secret}
                      </span>
                      <button
                        type="button"
                        onClick={handleCopySecret}
                        className="flex-shrink-0 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        {copied
                          ? <CheckCircle2 size={16} className="text-emerald-400" />
                          : <Copy size={16} />
                        }
                      </button>
                    </div>
                    <p className="text-gray-500 text-[10px]">
                      {t('LoginModal.scan_hint_after_entering')}
                    </p>
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleSetupVerify} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-500 rounded-2xl text-[11px] font-bold italic">
                  {error}
                </div>
              )}
              <input
                type="text"
                placeholder="000000"
                value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full bg-gray-50 border border-gray-200 p-6 rounded-sm text-gray-900 outline-none focus:border-brand-accent/60 font-mono text-2xl text-center tracking-[0.5em]"
                maxLength={6}
                required
                autoFocus
              />
              <Button
                type="submit"
                loading={isLoading}
                disabled={totpCode.length !== 6}
                size="xl"
                className="w-full"
              >
                {t('LoginModal.confirm')}
              </Button>
            </form>
          </>
        )}

        {step === 'totp' && (
          <>
            <div className="inline-flex p-6 bg-brand-accent rounded-xl mb-10 text-gray-900 shadow-xl shadow-brand-accent/30">
              <Shield size={40} />
            </div>
            <h2 className="text-3xl font-black text-gray-900 tracking-tighter uppercase italic leading-none mb-4">{t('LoginModal.two_factor_authentication')}</h2>
            <p className="text-gray-500 text-sm mb-10">{t('LoginModal.enter_6_digit_code_hint')}</p>
            <form onSubmit={handleTotp} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-500 rounded-2xl text-[11px] font-bold italic">
                  {error}
                </div>
              )}
              <input
                type="text"
                placeholder="000000"
                value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full bg-gray-50 border border-gray-200 p-6 rounded-sm text-gray-900 outline-none focus:border-brand-accent/60 font-mono text-2xl text-center tracking-[0.5em]"
                maxLength={6}
                required
                autoFocus
              />
              <Button
                type="submit"
                loading={isLoading}
                disabled={totpCode.length !== 6}
                size="xl"
                className="w-full"
              >
                {t('LoginModal.confirm')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => { setStep('credentials'); setError(''); setTotpCode(''); setPreAuthToken(''); }}
                className="w-full"
              >
                {t('LoginModal.back')}
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
};
